import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompanyMutation } from "@/hooks/useCompanyMutation";
import { supabase } from "@/integrations/supabase/client";
import { differenceInDays } from "date-fns";
import { AlertCircle, Clock, Medal, Star } from "lucide-react";

// --- Tipos ---
export type Customer = {
  id: string;
  name: string;
  phone: string;
  email: string;
  created_at: string;
};
export type Vehicle = {
  id: string;
  customer_id: string;
  plate: string;
  model: string;
  color: string;
};
export type Order = {
  id: string;
  customer_id: string;
  total: number;
  created_at: string;
  status: string;
  services: any[];
};

export function getCustomerMetrics(customerId: string, orders: Order[]) {
  const custOrders = orders
    .filter((o) => o.customer_id === customerId)
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  const totalSpent = custOrders.reduce((sum, o) => sum + (o.total || 0), 0);
  const visits = custOrders.length;
  const lastVisit =
    custOrders.length > 0 ? new Date(custOrders[0].created_at) : null;
  const daysSinceLastVisit = lastVisit
    ? differenceInDays(new Date(), lastVisit)
    : 999;

  let tag = {
    label: "Novo (Bronze)",
    color: "bg-orange-900/40 text-orange-400 border-orange-900",
    icon: Star,
  };

  if (daysSinceLastVisit > 60 && visits > 0) {
    tag = {
      label: "Em Risco (Ausente)",
      color: "bg-red-900/40 text-red-400 border-red-900",
      icon: AlertCircle,
    };
  } else if (visits >= 5 || totalSpent >= 500) {
    tag = {
      label: "VIP (Ouro)",
      color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      icon: Medal,
    };
  } else if (visits >= 3 || totalSpent >= 200) {
    tag = {
      label: "Frequente (Prata)",
      color: "bg-slate-300/20 text-slate-300 border-slate-500",
      icon: Star,
    };
  } else if (visits === 0) {
    tag = {
      label: "Sem Histórico",
      color: "bg-slate-800 text-slate-500 border-slate-700",
      icon: Clock,
    };
  }

  return { totalSpent, visits, lastVisit, custOrders, tag };
}

export function handleExportCustomerData(
  customer: Customer,
  vehicles: Vehicle[],
  orders: Order[],
) {
  const custVehicles = vehicles.filter((v) => v.customer_id === customer.id);
  const custOrders = orders.filter((o) => o.customer_id === customer.id);

  const dataStr = JSON.stringify(
    {
      title: "Relatório de Portabilidade e Acesso (LGPD)",
      generated_at: new Date().toISOString(),
      customer: {
        name: customer.name,
        phone: customer.phone,
        email: customer.email,
        created_at: customer.created_at,
      },
      vehicles: custVehicles.map((v) => ({
        plate: v.plate,
        model: v.model,
        color: v.color,
      })),
      orders_history: custOrders.map((o) => ({
        id: o.id,
        created_at: o.created_at,
        status: o.status,
        total: o.total,
        services: o.services,
      })),
    },
    null,
    2,
  );

  const blob = new Blob([dataStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.download = `dados_lgpd_${customer.name.toLowerCase().replace(/\s+/g, "_")}.json`;
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
}

export function useCustomerData(activeCompanyId: string | null) {
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading: loadingC } = useQuery({
    queryKey: ["customers", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("company_id", activeCompanyId)
        .order("name");
      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!activeCompanyId,
  });

  const { data: vehicles = [], isLoading: loadingV } = useQuery({
    queryKey: ["vehicles", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("vehicles")
        .select("*")
        .eq("company_id", activeCompanyId);
      if (error) throw error;
      return data as Vehicle[];
    },
    enabled: !!activeCompanyId,
  });

  const { data: orders = [], isLoading: loadingO } = useQuery({
    queryKey: ["customer_orders", activeCompanyId],
    queryFn: async () => {
      if (!activeCompanyId) return [];
      const { data, error } = await supabase
        .from("orders")
        .select("id, customer_id, total, created_at, status, services")
        .eq("company_id", activeCompanyId)
        .eq("status", "PAGO");
      if (error) throw error;
      return data as Order[];
    },
    enabled: !!activeCompanyId,
  });

  const saveCustomer = useCompanyMutation({
    mutationFn: async (vars: {
      id?: string;
      name: string;
      phone: string;
      email: string;
      plate?: string;
      model?: string;
      color?: string;
      company_id?: string;
    }) => {
      let customerId = vars.id;

      if (vars.id) {
        const { error } = await supabase
          .from("customers")
          .update({ name: vars.name, phone: vars.phone, email: vars.email })
          .eq("id", vars.id);
        if (error) throw error;
      } else {
        customerId = crypto.randomUUID();
        const { error } = await supabase.from("customers").insert({
          id: customerId,
          company_id: vars.company_id,
          name: vars.name,
          phone: vars.phone,
          email: vars.email,
        });
        if (error) throw error;
      }

      if (vars.plate && vars.model && customerId) {
        const { error: vError } = await supabase.from("vehicles").insert({
          id: crypto.randomUUID(),
          company_id: vars.company_id,
          customer_id: customerId,
          plate: vars.plate,
          model: vars.model,
          color: vars.color || "",
        });
        if (vError) throw vError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    },
  });

  const saveVehicle = useCompanyMutation({
    mutationFn: async (vars: {
      id?: string;
      customer_id: string;
      plate: string;
      model: string;
      color: string;
      company_id?: string;
    }) => {
      if (vars.id) {
        const { error } = await supabase
          .from("vehicles")
          .update({
            plate: vars.plate,
            model: vars.model,
            color: vars.color,
          })
          .eq("id", vars.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("vehicles").insert({
          id: crypto.randomUUID(),
          company_id: vars.company_id,
          customer_id: vars.customer_id,
          plate: vars.plate,
          model: vars.model,
          color: vars.color,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
    },
  });

  return {
    customers,
    vehicles,
    orders,
    loading: loadingC || loadingV || loadingO,
    saveCustomer,
    saveVehicle,
  };
}
